import { useState, useEffect } from "react";
import { toast } from "sonner";
import { StickyNote, Plus, Trash2, Pencil, Check, X, Lock, Pin } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";

export default function Notes() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [notes, setNotes] = useState([]);
  const [draft, setDraft] = useState("");
  const [editId, setEditId] = useState(null);
  const [editText, setEditText] = useState("");

  useEffect(() => { load(); }, []);
  async function load() {
    try { const { data } = await api.get("/staff/notes"); setNotes(data); } catch { /* */ }
  }

  async function add() {
    if (!draft.trim()) return;
    try { await api.post("/staff/notes", { content: draft.trim() }); setDraft(""); toast.success("Note added."); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed."); }
  }
  async function saveEdit(id) {
    try { await api.patch(`/staff/notes/${id}`, { content: editText }); setEditId(null); toast.success("Note updated."); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed."); }
  }
  async function del(id) {
    try { await api.delete(`/staff/notes/${id}`); toast.success("Note deleted."); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed."); }
  }
  async function togglePin(id) {
    try { await api.patch(`/staff/notes/${id}/pin`); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed."); }
  }

  const mine = notes.filter((n) => n.owner_id === user?.id);
  const others = notes.filter((n) => n.owner_id !== user?.id); // admins see worker notes here

  function NoteCard({ n, canManage }) {
    return (
      <div className={`rounded-xl border bg-card p-4 ${n.pinned ? "border-primary/50 ring-1 ring-primary/20" : "border-border"}`} data-testid={`note-${n.id}`}>
        {editId === n.id ? (
          <div className="space-y-2">
            <Textarea value={editText} onChange={(e) => setEditText(e.target.value)} rows={3} data-testid={`note-edit-text-${n.id}`} />
            <div className="flex gap-2">
              <Button size="sm" className="rounded-full" onClick={() => saveEdit(n.id)} data-testid={`note-save-${n.id}`}><Check className="mr-1 h-3 w-3" /> Save</Button>
              <Button size="sm" variant="ghost" className="rounded-full" onClick={() => setEditId(null)}><X className="mr-1 h-3 w-3" /> Cancel</Button>
            </div>
          </div>
        ) : (
          <>
            <p className="whitespace-pre-wrap text-sm">{n.pinned && <Pin className="mr-1 inline h-3.5 w-3.5 fill-primary text-primary" />}{n.content}</p>
            <div className="mt-3 flex items-center justify-between">
              <span className="text-xs text-muted-foreground">{n.owner_name}{n.owner_role === "admin" && <Badge variant="outline" className="ml-2 text-[10px]">admin</Badge>} · {(n.updated_at || "").slice(0, 10)}</span>
              {canManage && (
                <div className="flex gap-1">
                  <Button size="icon" variant="ghost" className={`h-7 w-7 ${n.pinned ? "text-primary" : ""}`} onClick={() => togglePin(n.id)} data-testid={`note-pin-${n.id}`}><Pin className={`h-3.5 w-3.5 ${n.pinned ? "fill-primary" : ""}`} /></Button>
                  {n.owner_id === user?.id && <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => { setEditId(n.id); setEditText(n.content); }} data-testid={`note-editbtn-${n.id}`}><Pencil className="h-3.5 w-3.5" /></Button>}
                  <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => del(n.id)} data-testid={`note-del-${n.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="notes-page">
      <div className="flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-secondary text-primary"><StickyNote className="h-6 w-6" /></div>
        <div>
          <h1 className="font-heading text-2xl font-bold">Notes</h1>
          <p className="text-sm text-muted-foreground">{isAdmin ? "Your private notes plus every worker's notes. Your admin notes stay hidden from workers." : "Your personal notes. Admins can see your notes; you can't see admins' notes."}</p>
        </div>
      </div>

      <div className="rounded-xl border border-border bg-card p-4">
        <Textarea placeholder="Write a note..." value={draft} onChange={(e) => setDraft(e.target.value)} rows={3} data-testid="note-new-text" />
        <div className="mt-2 flex items-center justify-between">
          <span className="flex items-center gap-1 text-xs text-muted-foreground"><Lock className="h-3 w-3" /> {isAdmin ? "Visible to admins only" : "Visible to you and admins"}</span>
          <Button className="rounded-full" onClick={add} data-testid="note-add-btn"><Plus className="mr-1 h-4 w-4" /> Add note</Button>
        </div>
      </div>

      <div>
        <h2 className="mb-3 font-heading text-lg font-bold">Your notes</h2>
        {mine.length === 0 ? <p className="rounded-xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">No notes yet.</p>
          : <div className="grid gap-3 sm:grid-cols-2">{mine.map((n) => <NoteCard key={n.id} n={n} canManage />)}</div>}
      </div>

      {isAdmin && (
        <div>
          <h2 className="mb-3 font-heading text-lg font-bold">Worker notes</h2>
          {others.length === 0 ? <p className="rounded-xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">No worker notes yet.</p>
            : <div className="grid gap-3 sm:grid-cols-2">{others.map((n) => <NoteCard key={n.id} n={n} canManage />)}</div>}
        </div>
      )}
    </div>
  );
}
